import React, { useEffect } from "react";
import { Prompt } from "react-router-dom";
import { Grid, Backdrop, Button, Box } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IVehicleType } from "src/common/typings/vehicle";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import { columns } from "./columns";
import { setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IVehicleType {}

interface Props {
  // In TS we make an interface of Props to shape them.
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IVehicleType; // '?' Its means initialValues is Optional
  loading?: any;

  tableRecord?: any;
  temp?: any;
}

export function InputFields({
  initialValues,
  handleSubmit,
  updateSubmit,
  loading,
  tableRecord,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [isEditMode, setisEditMode] = React.useState(false);
  const submitCounter = 0;
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const validationSchema = object({
    name: string().required("This field is required").max(30, "Maximum 30 characters are allowed."),
  });
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      id: initialValues?.id || 0,
      name: initialValues?.name || "",
      isActive: initialValues?.isActive || Boolean(false),
    },
    validationSchema: validationSchema,
    // onSubmit: handleSubmit,
    onSubmit: (values, actions) => {
      if (isEditMode === false && isDuplicatedRecordInTable(tableRecord, values.name) !== -1) {
        dispatch(
          addNotificationAction({
            message: "Record already exist.",
            options: { variant: "error" },
          })
        );
        return;
      }
      if (isEditMode) updateSubmit(values);
      else {
        formik.setFieldValue("id", 0);
        handleSubmit(values);
      }
      actions.setSubmitting(false);
      actions.resetForm();
      setisEditMode(false);
      setIsDirtyValue(false);
    },
  });
  const handleUpdateClick = (params: any) => {
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("isActive", params.isActive);
    setisEditMode(true);
  };
  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);
  const isDuplicatedRecordInTable = (array: any, value: any) => {
    return array.findIndex((i: any) => i.name === value);
  };
  /* GRID Code */
  useEffect(() => {
    if (tableRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [tableRecord]);

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid
                  container
                  item
                  xs={12}
                  md={9}
                  lg={7}
                  spacing={3}
                  style={{ paddingTop: "20px" }}
                >
                  <CommonMaterialGrid
                    defaultPerPageRecord={10}
                    height={400}
                    loader={false}
                    rows={tableRecord || []}
                    columns={displayColumns}
                  />
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* END Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
